import { RootStore } from '@/app/store'
import {
  PluginCallStatus,
  processEventStream,
  ProcessEventStreamOptions,
  StreamEvent,
} from '@/utils/stream'
import { action, makeAutoObservable, runInAction } from 'mobx'
import {
  addFeedbackToMessage,
  createThread,
  fetchThread,
  revertToMessage,
} from '../tools/api'
import { Message, Suggestion, Thread } from '../tools/types'
import { makeEmptyThread } from './utils'
import { API_ROUTE } from '../backend/config'
import { fetchJSON } from '@/utils/http'
import { queryClient } from '@/lib/react-query'

export class ChatStore {
  rootStore: RootStore
  thread: Thread
  loading?: boolean
  suggestions: Suggestion[] = []

  // Mapping of message indices to their completion status
  private uncommittedMessages: Map<number, { completed: boolean }> = new Map()

  private focusInputCallback: (() => void) | null = null

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
    this.thread = makeEmptyThread()
  }

  public registerFocusInputCallback(callback: () => void) {
    this.focusInputCallback = callback
  }

  public unregisterFocusInputCallback() {
    this.focusInputCallback = null
  }

  private triggerFocusInput() {
    if (this.focusInputCallback) {
      // Use setTimeout to ensure DOM has updated before focusing
      setTimeout(() => {
        this.focusInputCallback?.()
      }, 0)
    }
  }

  public get hasPendingPlugins() {
    return this.thread.messages.some(
      (message) =>
        message.role === 'assistant' &&
        message.content.some(
          (content) =>
            content.type === 'pluginCall' &&
            content.status === PluginCallStatus.pending,
        ),
    )
  }

  // NOTE: Called on logout
  public resetState() {
    this.thread = makeEmptyThread()
    this.loading = undefined
    this.suggestions = []
    this.uncommittedMessages.clear()
    this.rootStore.artifacts?.resetState()
  }

  public async loadThread(id?: string) {
    this.loading = true

    try {
      if (!id) {
        this.loadEmptyThread()
        return
      }

      // NOTE: Thread is already loaded
      if (this.thread._id === id) {
        return
      } else {
        // NOTE: Clear thread before loading a new one
        this.clearThread()
      }

      const thread = await fetchThread(id)

      runInAction(() => {
        this.thread = thread
      })

      return thread
    } catch (error) {
      console.error(error)
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  public loadEmptyThread() {
    this.thread = makeEmptyThread()
    this.rootStore.artifacts?.closeArtifactViewer()
  }

  public async maybeCreateThread(
    toolId: string,
    projectId?: string,
    articleId?: string,
  ) {
    if (!this.thread._id) {
      // Preserve local messages before creating thread
      const localMessages = [...this.thread.messages]

      const thread = await createThread(toolId, projectId, articleId)
      runInAction(() => {
        // Merge the thread from backend with local messages
        this.thread = {
          ...thread,
          messages: localMessages,
        }
      })

      // Invalidate history cache to update the sidebar
      queryClient.invalidateQueries(['recent-chats'])
      queryClient.refetchQueries(['recent-chats'])

      // Persist any uncommitted messages (like initial article content)
      await this.persistUncommittedMessages()
    }
  }

  public clearThread() {
    this.thread = makeEmptyThread()
    this.uncommittedMessages.clear()
    this.rootStore.artifacts?.closeArtifactViewer()
  }

  public async addMessage(message: Message) {
    const messageIndex = this.thread.messages.length
    this.thread.messages = [...this.thread.messages, message]

    if (
      this.rootStore.artifacts.artifactViewerOpen &&
      this.rootStore.artifacts.artifactViewMode === 'single-column'
    ) {
      this.rootStore.artifacts.toggleArtifactViewMode()
    }

    // Mark initial messages as uncommitted so they get persisted when thread is created
    if (message.initialMessage) {
      this.uncommittedMessages.set(messageIndex, { completed: true })
    }

    // Focus input when assistant message is added (but not if it's empty - streaming initial state)
    if (message.role === 'assistant' && message.content.length > 0) {
      this.triggerFocusInput()
    }
  }

  public async revertToMessage(messageIndex: number) {
    this.thread.messages = this.thread.messages.slice(0, messageIndex + 1)
    await revertToMessage(this.thread._id!, messageIndex)
  }

  public async addFeedbackToMessage(
    messageIndex: number,
    feedback: { liked?: boolean },
  ) {
    await addFeedbackToMessage(this.thread._id!, messageIndex, feedback)
  }

  async ingestMessageStream(
    options: ProcessEventStreamOptions,
    // eslint-disable-next-line no-unused-vars
    onEvent?: (event: StreamEvent) => void,
  ) {
    this.addMessage({
      role: 'assistant',
      content: [],
    })

    await processEventStream(
      options,
      action((message: Message) => {
        this.thread.messages[this.thread.messages.length - 1] = message
      }),
      onEvent,
    )

    // Focus input after streaming completes
    this.triggerFocusInput()
  }

  async setSuggestions(suggestions: Suggestion[]) {
    this.suggestions = suggestions
  }

  // Voice chat message streaming methods

  /**
   * Creates an uncommitted message entry in the thread
   * @param role The role of the message ('user' or 'assistant')
   * @returns The index of the message in the thread
   */
  public createUncommittedMessage(
    role: 'user' | 'assistant',
    voiceMessage?: boolean,
  ): number {
    // Add an empty message to the thread
    this.addMessage({
      role,
      content: [{ type: 'text', text: '', isTranscription: voiceMessage }],
    })

    const messageIndex = this.thread.messages.length - 1

    this.uncommittedMessages.set(messageIndex, { completed: false })

    return messageIndex
  }

  /**
   * Appends content to an uncommitted message
   * @param messageIndex The index of the message in the thread
   * @param text The text to append to the message
   */
  appendToUncommittedMessage(messageIndex: number, text: string): void {
    if (messageIndex < 0 || messageIndex >= this.thread.messages.length) {
      return
    }

    if (!this.uncommittedMessages.has(messageIndex)) {
      return
    }

    const message = this.thread.messages[messageIndex]
    const currentContent = message.content[0]

    const newContent = (currentContent?.text || '') + text

    runInAction(() => {
      this.thread.messages[messageIndex].content = [
        { ...currentContent, text: newContent },
      ]
    })
  }

  /**
   * Marks a message as completed and ready for persistence
   * @param messageIndex The index of the message to mark as completed
   */
  commitMessage(messageIndex: number, fullMessage?: string): void {
    if (messageIndex < 0 || messageIndex >= this.thread.messages.length) {
      return
    }

    if (!this.uncommittedMessages.has(messageIndex)) {
      return
    }

    runInAction(() => {
      this.uncommittedMessages.set(messageIndex, { completed: true })

      if (fullMessage) {
        const message = this.thread.messages[messageIndex]
        if (message && message.content && message.content[0]) {
          // Mutate the existing content object directly instead of replacing the array
          message.content[0].text = fullMessage
        }
      }
    })
  }

  /**
   * Persists all completed uncommitted messages in order
   * Stops persisting when it encounters an incomplete message
   */
  async persistUncommittedMessages(): Promise<void> {
    if (!this.thread._id) return

    // Get all uncommitted message indices sorted in ascending order (chronological)
    const sortedIndices = Array.from(this.uncommittedMessages.keys()).sort(
      (a, b) => a - b,
    )

    if (sortedIndices.length === 0) return

    const messagesToPersist = []

    // Process messages in order until we find an incomplete message
    for (const index of sortedIndices) {
      const messageState = this.uncommittedMessages.get(index)

      // Stop when we find an incomplete message to preserve order
      if (!messageState?.completed) {
        break
      }

      const message = this.thread.messages[index]

      if (!message) {
        console.warn(`Message at index ${index} not found`)
        continue
      }

      messagesToPersist.push(message)

      // Remove from the uncommitted messages map
      this.uncommittedMessages.delete(index)
    }

    if (messagesToPersist.length === 0) return

    try {
      // Call the API endpoint to append messages to the thread
      await fetchJSON(`${API_ROUTE}/tools/threads/${this.thread._id}`, {
        method: 'POST',
        body: JSON.stringify({ messages: messagesToPersist }),
      })
    } catch (error) {
      console.error('Error persisting messages:', error)
    }
  }
}
